import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams, Link, useSearchParams, useLocation } from "react-router-dom";
import { api, formatApiError, extractFieldErrors, formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Search, Plus, Check, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import TemplateMultiSelect from "@/components/TemplateMultiSelect";
import { TagChip } from "@/components/TagChip";
import { TravelPreview } from "@/components/TravelPreview";
import ConflictWarningDialog from "@/components/ConflictWarningDialog";
import MapsLinkHelper from "@/components/MapsLinkHelper";

function CustomerStep({ value, onSelect }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newCust, setNewCust] = useState({ company_name: "", contact_name: "", phone: "" });
  // Phase 50.4 — soft duplicate-phone warning state. Same UX as
  // QuickBookingForm: surface a warning row with "Use existing" /
  // "Create new anyway" buttons; never block the manager.
  const [dupCustomer, setDupCustomer] = useState(null);

  const _normPhone = (raw) => {
    const t = String(raw || "").trim();
    if (!t) return "";
    const lead = t[0] === "+" ? "+" : "";
    return lead + t.replace(/\D/g, "");
  };
  const _phoneValid = (raw) => _normPhone(raw).replace(/\D/g, "").length >= 7;
  const checkPhoneForDuplicate = async () => {
    const norm = _normPhone(newCust.phone);
    if (!_phoneValid(norm)) { setDupCustomer(null); return; }
    try {
      const { data } = await api.get("/customers/search", { params: { phone: norm } });
      const list = Array.isArray(data) ? data : [];
      setDupCustomer(list[0] || null);
    } catch { setDupCustomer(null); }
  };

  const search = useCallback(async (term) => {
    if (!term?.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      const { data } = await api.get("/customers/search", { params: { phone: term } });
      let extras = [];
      try {
        const { data: d2 } = await api.get("/customers", { params: { q: term } });
        extras = d2.filter((c) => !data.some((x) => x.id === c.id));
      } catch {/* ignore */}
      setResults([...data, ...extras]);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(q), 250);
    return () => clearTimeout(t);
  }, [q, search]);

  const create = async () => {
    // Phase P.4 Item 1 — accept phone-only. Company name becomes optional
    // (backend now flags details_pending=true when company/email missing).
    if (!_phoneValid(newCust.phone)) {
      toast.error("Phone (≥7 digits) is required");
      return;
    }
    try {
      const { data } = await api.post("/customers", {
        company_name: newCust.company_name.trim(),
        contact_name: newCust.contact_name.trim(),
        phones: [_normPhone(newCust.phone)],
        emails: [],
        notes: "",
      });
      toast.success(data.details_pending ? "Customer saved — details to follow" : "Customer created");
      onSelect(data);
      setShowCreate(false);
      setDupCustomer(null);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  if (value) {
    return (
      <div className="bg-secondary/40 border border-border rounded-[6px] p-3 flex items-center justify-between gap-3" data-testid="selected-customer">
        <div className="min-w-0 flex-1">
          <div className="font-bold">{value.company_name}</div>
          <div className="text-xs text-muted-foreground truncate">{value.contact_name} · {value.phones?.[0]}</div>
          {value.tags?.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1" data-testid="selected-customer-tags">
              {value.tags.map((t) => <TagChip key={t.id} tag={t} size="xs" />)}
            </div>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={() => onSelect(null)} data-testid="change-customer-button">Change</Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by phone or name..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9 h-11"
          data-testid="customer-search-input"
        />
      </div>
      {q && results.length > 0 && (
        <div className="border border-border rounded-[6px] divide-y divide-border max-h-64 overflow-y-auto" data-testid="customer-results">
          {results.map((c) => (
            <button
              type="button"
              key={c.id}
              onClick={() => onSelect(c)}
              className="w-full p-3 text-left hover:bg-secondary/60 transition-colors"
              data-testid={`customer-result-${c.id}`}
            >
              <div className="font-semibold text-sm">{c.company_name}</div>
              <div className="text-xs text-muted-foreground">{c.contact_name} · {c.phones?.[0]}</div>
            </button>
          ))}
        </div>
      )}
      {q && !searching && results.length === 0 && (
        <div className="text-sm text-muted-foreground">No matches.</div>
      )}

      {!showCreate ? (
        <Button type="button" variant="outline" size="sm" onClick={() => setShowCreate(true)} data-testid="create-new-customer-toggle">
          <Plus className="h-3 w-3 mr-1" /> Create new customer
        </Button>
      ) : (
        <div className="bg-card border border-border rounded-[6px] p-3 space-y-2" data-testid="inline-create-customer">
          {/* Phase 50.4 — Phone first: it's the primary identifier per
              the original PRD. Soft duplicate warning appears below
              once a valid phone is entered + blurred. */}
          <Input
            placeholder="Phone *"
            type="tel"
            inputMode="tel"
            value={newCust.phone}
            onChange={(e) => {
              setNewCust({ ...newCust, phone: e.target.value });
              if (dupCustomer) setDupCustomer(null);
            }}
            onBlur={checkPhoneForDuplicate}
            data-testid="new-customer-phone-input"
          />
          {dupCustomer && (
            <div
              className="border border-amber-300 bg-amber-50 rounded-md p-2 text-[12px] text-amber-900 space-y-2"
              data-testid="new-customer-dup-warning"
            >
              <div>
                A customer with this phone already exists:{" "}
                <span className="font-bold">
                  {dupCustomer.company_name || dupCustomer.contact_name}
                </span>.
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px] border-amber-400 bg-white"
                  onClick={() => {
                    onSelect(dupCustomer);
                    setShowCreate(false);
                    setDupCustomer(null);
                    setNewCust({ company_name: "", contact_name: "", phone: "" });
                  }}
                  data-testid="new-customer-use-existing"
                >
                  Use existing
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 text-[11px] text-amber-900 hover:bg-amber-100"
                  onClick={() => setDupCustomer(null)}
                  data-testid="new-customer-create-anyway"
                >
                  Create new anyway
                </Button>
              </div>
            </div>
          )}
          <Input placeholder="Company name (optional — fill in later)" value={newCust.company_name} onChange={(e) => setNewCust({ ...newCust, company_name: e.target.value })} data-testid="new-customer-company-input" />
          <Input placeholder="Contact name" value={newCust.contact_name} onChange={(e) => setNewCust({ ...newCust, contact_name: e.target.value })} data-testid="new-customer-contact-input" />
          {/* Phase P.4 Item 1 — when the manager is about to save with
              phone-only, surface a small helper so they know the customer
              will be marked details_pending server-side. */}
          {!newCust.company_name.trim() && _phoneValid(newCust.phone) && (
            <div
              className="text-[11px] text-red-700 border border-red-200 bg-red-50 rounded px-2 py-1"
              data-testid="quick-add-customer-details-pending-note"
            >
              Customer will be saved with phone only — add company + email later on their profile.
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <Button type="button" size="sm" onClick={create} className="btn-cta" data-testid="new-customer-save-button">
              <Check className="h-3 w-3 mr-1" /> Save & select
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Phase 8: one empty line for "New booking" initial state.
function emptyLine() {
  return { template_id: "", description: "", unit_price: "", duration_minutes: "", quantity: 1 };
}

function LineItemRow({ idx, line, templates, onChange, onRemove, canRemove, currency }) {
  const selectValue = line.template_id || "__adhoc__";
  return (
    <div className="grid grid-cols-12 gap-2 items-start bg-card border border-border rounded-[6px] p-3" data-testid={`line-item-row-${idx}`}>
      <div className="col-span-12 md:col-span-4">
        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Service</Label>
        <Select
          value={selectValue}
          onValueChange={(v) => {
            if (v === "__adhoc__") {
              onChange({ ...line, template_id: "" });
              return;
            }
            const t = templates.find((x) => x.id === v);
            if (!t) return;
            onChange({
              ...line,
              template_id: t.id,
              description: t.name,
              unit_price: String(t.default_price ?? ""),
              duration_minutes: String(t.default_duration_minutes ?? ""),
            });
          }}
        >
          <SelectTrigger className="mt-1" data-testid={`line-template-select-${idx}`}>
            <SelectValue placeholder="Pick a template or ad-hoc" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__adhoc__" data-testid={`line-template-adhoc-${idx}`}>Ad-hoc (no template)</SelectItem>
            {templates.map((t) => (
              <SelectItem key={t.id} value={t.id} data-testid={`line-template-option-${idx}-${t.id}`}>
                {t.name} · {formatCurrency(t.default_price, currency)} · {t.default_duration_minutes}m
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-12 md:col-span-3">
        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Description</Label>
        <Input
          value={line.description}
          onChange={(e) => onChange({ ...line, description: e.target.value })}
          placeholder="Service description"
          className="mt-1"
          data-testid={`line-description-${idx}`}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Qty</Label>
        <Input
          type="number"
          min="1"
          value={line.quantity}
          onChange={(e) => onChange({ ...line, quantity: e.target.value })}
          className="mt-1"
          data-testid={`line-quantity-${idx}`}
        />
      </div>
      <div className="col-span-4 md:col-span-2">
        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Unit price</Label>
        <Input
          type="number"
          step="0.01"
          min="0"
          value={line.unit_price}
          onChange={(e) => onChange({ ...line, unit_price: e.target.value })}
          className="mt-1 mono"
          data-testid={`line-unit-price-${idx}`}
        />
      </div>
      <div className="col-span-3 md:col-span-1">
        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Dur</Label>
        <Input
          type="number"
          min="0"
          step="15"
          value={line.duration_minutes}
          onChange={(e) => onChange({ ...line, duration_minutes: e.target.value })}
          className="mt-1"
          data-testid={`line-duration-${idx}`}
        />
      </div>
      <div className="col-span-9 md:col-span-1 flex items-end justify-end pt-1">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={!canRemove}
          onClick={onRemove}
          data-testid={`line-remove-${idx}`}
          title="Remove line"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <div className="col-span-12 flex items-center justify-between text-xs mt-1" data-testid={`line-total-${idx}`}>
        <span className="text-muted-foreground mono uppercase tracking-widest">Line total</span>
        <span className="mono font-bold">
          {formatCurrency((Number(line.unit_price) || 0) * (Number(line.quantity) || 0), currency)}
        </span>
      </div>
    </div>
  );
}

export default function BookingForm({ mode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = mode === "edit";

  // Phase P.3 Item 2 — smart back navigation. If the manager arrived here from
  // a specific page (Jobs, Accounts, InvoiceDetail, Calendar) via router
  // `state: {from: pathname}`, return there. Otherwise fall back to the
  // browser's own back-stack via `navigate(-1)` when there's history to pop.
  // Last resort: /calendar. This fixes the bug where saving a booking always
  // dumped the manager on the Calendar regardless of where they came from.
  const returnTo = location.state?.from;
  const backNav = () => {
    if (returnTo) { navigate(returnTo); return; }
    if (window.history.length > 1) { navigate(-1); return; }
    navigate("/calendar");
  };

  const [templates, setTemplates] = useState([]);
  const [staff, setStaff] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [paidMethods, setPaidMethods] = useState([]);  // Phase F
  const [customer, setCustomer] = useState(null);
  const [currency, setCurrency] = useState("EUR");
  const [requireLocation, setRequireLocation] = useState(true);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Phase 48 — per-field 422 outlines + scroll-to-first.
  const [fieldErrors, setFieldErrors] = useState({});
  const fieldRefs = useRef({});
  const notesRef = useRef(null);
  // Phase 48 — soft conflict warning state.
  const [warnOnTimeConflicts, setWarnOnTimeConflicts] = useState(true);
  const [conflictOpen, setConflictOpen] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const pendingPayloadRef = useRef(null);
  const pendingConfigRef = useRef(null);

  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [searchParams] = useSearchParams();
  const presetDate = searchParams.get("date");
  const enquiryId = searchParams.get("enquiryId");  // Phase 36 — converting enquiry → booking
  const [enquiry, setEnquiry] = useState(null);
  const initialDate = presetDate && /^\d{4}-\d{2}-\d{2}$/.test(presetDate) ? presetDate : todayStr;

  const [form, setForm] = useState({
    staff_id: "",
    date: initialDate,
    time: "09:00",
    duration: "",            // blank => auto-compute from lines; typed => manual override
    durationOverride: false,
    postcode: "",
    address_line: "",
    latlng: "",
    notes: "",
    payment_type: "",
    paid_method_code: "",  // Phase F — form-of-payment, optional
    discount_type: "none",
    discount_value: "0",
    discountOpen: false,
  });
  // Phase 19: when unchecked, the booking save call passes ?notify_staff=false so
  // the staff member doesn't get an email for this single change.
  const [notifyStaff, setNotifyStaff] = useState(true);
  const [lines, setLines] = useState([emptyLine()]);

  useEffect(() => {
    Promise.all([
      api.get("/templates"),
      api.get("/staff"),
      api.get("/settings").catch(() => ({ data: { currency: "EUR" } })),
      // Phase 9.1: in audit mode, hide non-accounts payment types (e.g. Simple Cash) from the dropdown.
      api.get("/settings").then(({ data }) =>
        api.get("/settings/payment-types", { params: data?.audit_mode ? { hide_non_accounts: true } : {} }),
      ).catch(() => ({ data: [] })),
      // Phase F — Paid Methods for the form-of-payment dropdown.
      api.get("/settings/paid-methods").catch(() => ({ data: [] })),
      isEdit ? api.get(`/bookings/${id}`) : Promise.resolve(null),
    ]).then(([t, s, st, pt, pm, b]) => {
      setTemplates(t.data.filter((x) => x.active));
      setStaff(s.data.filter((x) => x.active));
      setCurrency(st.data?.currency || "EUR");
      setRequireLocation(st.data?.require_location !== false);
      // Phase 48 — settings toggle controls the soft conflict warning.
      setWarnOnTimeConflicts(st.data?.warn_on_time_conflicts !== false);
      setPaymentTypes(pt.data);
      setPaidMethods(Array.isArray(pm.data) ? pm.data : []);
      const defaultPt = pt.data.find((p) => p.is_default)?.code || pt.data[0]?.code || "vat";
      if (b) {
        const bk = b.data;
        const dt = new Date(bk.scheduled_start);
        const autoDuration = (bk.line_items || []).reduce(
          (s, li) => s + Number(li.duration_minutes || 0) * Number(li.quantity || 1), 0);
        const isOverridden = Number(bk.scheduled_duration_minutes) !== autoDuration;
        setForm({
          staff_id: bk.staff_id,
          date: dt.toISOString().slice(0, 10),
          time: dt.toTimeString().slice(0, 5),
          duration: isOverridden ? String(bk.scheduled_duration_minutes || "") : "",
          durationOverride: isOverridden,
          postcode: bk.location?.postcode || bk.location?.eircode || "",
          address_line: bk.location?.address_line || "",
          latlng: bk.location?.lat && bk.location?.lng ? `${bk.location.lat},${bk.location.lng}` : "",
          notes: bk.notes || "",
          payment_type: bk.payment_type || defaultPt,
          paid_method_code: bk.paid_method_code || "",  // Phase F
          discount_type: bk.discount_type || "none",
          discount_value: String(bk.discount_value || "0"),
          discountOpen: (bk.discount_type && bk.discount_type !== "none"),
        });
        setLines((bk.line_items || [emptyLine()]).map((li) => ({
          id: li.id,
          template_id: li.template_id || "",
          description: li.description || "",
          unit_price: String(li.unit_price ?? ""),
          duration_minutes: String(li.duration_minutes ?? ""),
          quantity: Number(li.quantity || 1),
        })));
        api.get(`/customers/${bk.customer_id}`).then(({ data }) => setCustomer(data)).catch(() => {});
      } else {
        setForm((f) => ({ ...f, payment_type: defaultPt }));
      }
    }).catch((e) => toast.error(formatApiError(e?.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [isEdit, id]);

  // Customer default payment type — only on create, only if customer has one and no explicit choice yet.
  useEffect(() => {
    if (isEdit) return;
    if (!customer || paymentTypes.length === 0) return;
    const code = customer.default_payment_type_code;
    if (code && paymentTypes.some((p) => p.code === code)) {
      setForm((f) => ({ ...f, payment_type: code }));
    }
  }, [customer, paymentTypes, isEdit]);

  // Phase 36 — when ?enquiryId=… is present, pull the enquiry once and pre-populate
  // the form with its captured fields. The manager can still adjust everything
  // before save; the booking-confirm endpoint will mark the enquiry confirmed.
  useEffect(() => {
    if (isEdit || !enquiryId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: enq } = await api.get(`/booking-enquiries/${enquiryId}`);
        if (cancelled) return;
        setEnquiry(enq);
        // Date + time
        if (enq.tentative_start) {
          const d = new Date(enq.tentative_start);
          if (!isNaN(d.getTime())) {
            setForm((f) => ({
              ...f,
              date: d.toISOString().slice(0, 10),
              time: d.toTimeString().slice(0, 5),
            }));
          }
        }
        // Address (only address_line — postcode/lat/lng unknown from enquiry).
        if (enq.address) {
          setForm((f) => ({ ...f, address_line: enq.address || f.address_line }));
        }
        // Notes
        if (enq.notes) {
          setForm((f) => ({ ...f, notes: f.notes ? `${f.notes}\n\n${enq.notes}` : enq.notes }));
        }
        // Customer link
        if (enq.customer_id) {
          try {
            const { data } = await api.get(`/customers/${enq.customer_id}`);
            if (!cancelled) setCustomer(data);
          } catch { /* ignore */ }
        }
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e?.response?.data?.detail));
      }
    })();
    return () => { cancelled = true; };
  }, [enquiryId, isEdit]);

  // Totals (live) — match server-side math.
  const subtotalBefore = useMemo(() =>
    lines.reduce((s, l) => s + (Number(l.unit_price) || 0) * (Number(l.quantity) || 0), 0)
  , [lines]);
  const discountAmount = useMemo(() => {
    if (form.discount_type === "none") return 0;
    const v = Number(form.discount_value) || 0;
    if (v <= 0) return 0;
    if (form.discount_type === "amount") return Math.min(v, subtotalBefore);
    // percent
    const pct = Math.max(0, Math.min(100, v));
    return subtotalBefore * (pct / 100);
  }, [form.discount_type, form.discount_value, subtotalBefore]);
  const subtotal = Math.max(0, subtotalBefore - discountAmount);
  const selectedPt = paymentTypes.find((p) => p.code === form.payment_type);
  const taxRate = Number(selectedPt?.tax_rate || 0);
  const taxAmount = subtotal * (taxRate / 100);
  const grandTotal = subtotal + taxAmount;
  const autoDuration = useMemo(() =>
    lines.reduce((s, l) => s + (Number(l.duration_minutes) || 0) * (Number(l.quantity) || 1), 0)
  , [lines]);

  // Phase 32a — derive the values the <TravelPreview> needs. Memoised so
  // the debounce inside the preview component only re-fires on real input
  // changes (not every parent re-render).
  const previewIso = useMemo(() => {
    if (!form.date || !form.time) return "";
    try {
      return new Date(`${form.date}T${form.time}:00`).toISOString();
    } catch {
      return "";
    }
  }, [form.date, form.time]);
  const previewDuration = useMemo(() => {
    if (form.durationOverride && form.duration !== "") return Number(form.duration) || 60;
    return autoDuration || 60;
  }, [form.durationOverride, form.duration, autoDuration]);
  const previewLatLng = useMemo(() => {
    if (!form.latlng?.trim()) return { lat: null, lng: null };
    const parts = form.latlng.split(",").map((s) => parseFloat(s.trim()));
    if (parts.length === 2 && parts.every(Number.isFinite)) {
      return { lat: parts[0], lng: parts[1] };
    }
    return { lat: null, lng: null };
  }, [form.latlng]);

  const updateLine = (i, next) => {
    const arr = [...lines];
    arr[i] = next;
    setLines(arr);
  };
  const removeLine = (i) => {
    if (lines.length <= 1) return;
    setLines(lines.filter((_, idx) => idx !== i));
  };
  const addLine = () => setLines([...lines, emptyLine()]);

  const submit = async (e) => {
    e.preventDefault();
    if (!customer) return toast.error("Pick a customer");
    if (!form.staff_id) return toast.error("Pick a staff member");
    if (!form.date || !form.time) return toast.error("Date and time are required");
    if (lines.length < 1) return toast.error("At least one service line is required");
    for (const [i, l] of lines.entries()) {
      const hasTpl = !!l.template_id;
      const price = Number(l.unit_price);
      const dur = Number(l.duration_minutes);
      if (!hasTpl) {
        if (!l.description?.trim()) return toast.error(`Line ${i + 1}: description is required`);
        if (!Number.isFinite(price) || price < 0) return toast.error(`Line ${i + 1}: unit price is required`);
        if (!Number.isFinite(dur) || dur <= 0) return toast.error(`Line ${i + 1}: duration is required`);
      }
    }

    const iso = new Date(`${form.date}T${form.time}:00`).toISOString();
    let lat = null, lng = null;
    if (form.latlng?.trim()) {
      const parts = form.latlng.split(",").map((s) => parseFloat(s.trim()));
      if (parts.length === 2 && parts.every(Number.isFinite)) [lat, lng] = parts;
    }
    const payloadLines = lines.map((l) => ({
      template_id: l.template_id || null,
      description: l.description || undefined,
      unit_price: l.unit_price === "" ? undefined : Number(l.unit_price),
      duration_minutes: l.duration_minutes === "" ? undefined : Number(l.duration_minutes),
      quantity: Number(l.quantity) || 1,
    }));
    const payload = {
      customer_id: customer.id,
      staff_id: form.staff_id,
      scheduled_start: iso,
      line_items: payloadLines,
      ...(requireLocation
        ? { location: { postcode: form.postcode.trim(), address_line: form.address_line.trim(), lat, lng } }
        : {}),
      notes: form.notes,
      payment_type: form.payment_type,
      // Phase F — form-of-payment is optional; empty string is treated as
      // "unset" by the backend (clears any prior link).
      paid_method_code: form.paid_method_code || null,
      discount_type: form.discount_type,
      discount_value: Number(form.discount_value) || 0,
    };
    if (form.durationOverride && form.duration !== "") {
      payload.scheduled_duration_minutes = Number(form.duration);
    }

    const config = { params: { notify_staff: notifyStaff } };

    // Phase 48 — soft conflict check before commit. Skipped entirely
    // (no network round-trip) when the manager has toggled
    // `warn_on_time_conflicts` off in Settings.
    if (warnOnTimeConflicts) {
      try {
        const checkDuration = payload.scheduled_duration_minutes
          || autoDuration
          || 60;
        const { data: check } = await api.post("/bookings/check-conflicts", {
          staff_id: form.staff_id,
          customer_id: customer.id,
          scheduled_start: iso,
          duration_minutes: checkDuration,
          location: payload.location || null,
          booking_id_to_exclude: isEdit ? id : null,
        });
        if (check?.settings_enabled && (check.conflicts || []).length > 0) {
          pendingPayloadRef.current = payload;
          pendingConfigRef.current = config;
          setConflicts(check.conflicts);
          setConflictOpen(true);
          return;
        }
      } catch { /* non-fatal — fall through to actual save */ }
    }

    setSubmitting(true);
    try {
      await commitSave(payload, config);
    } catch (e2) {
      handleSaveError(e2);
    } finally {
      setSubmitting(false);
    }
  };

  // Phase 48 — extracted commit so the conflict modal can re-fire on
  // "Save anyway". Always navigates to /calendar on success.
  const commitSave = async (payload, config) => {
    let res;
    if (isEdit) {
      res = await api.put(`/bookings/${id}`, payload, config);
      toast.success(`Updated ${res.data.booking_code}`);
      // Phase 37 — surface the invoice-sync prompt if the booking already
      // has an attached invoice. BookingOut doesn't carry invoice_id; we
      // resolve via the invoices list filtered by booking_id.
      try {
        const { data: invs } = await api.get("/invoices", { params: { booking_id: id } });
        const linkedInv = (invs || []).find((i) => i.booking_id === id);
        if (linkedInv) {
          const lockedTerminal = linkedInv.is_locked
            || linkedInv.status === "voided"
            || ["paid", "write_off", "unable_to_collect"].includes(linkedInv.payment_status);
          const goTo = () => navigate(`/invoices/${linkedInv.id}?booking_changed=1`);
          if (lockedTerminal) {
            toast.warning(
              `Booking updated, but invoice ${linkedInv.invoice_number} is ${linkedInv.status === "voided" ? "voided" : "paid/settled"}. Changes won${"\u2019"}t affect it — create a new invoice if needed.`,
              { duration: 8000, action: { label: "View invoice", onClick: goTo } },
            );
          } else {
            toast.warning(
              `This booking has an unpaid invoice (${linkedInv.invoice_number} rev ${linkedInv.revision || 1}). Regenerate the invoice to reflect these changes?`,
              { duration: 10000, action: { label: "View invoice", onClick: goTo } },
            );
          }
        }
      } catch { /* ignore — fall through to default nav */ }
    } else if (enquiryId) {
      // Phase 36 — convert an enquiry → booking via the confirm endpoint.
      // The booking_payload is wrapped; server-side merges the enquiry's
      // captured defaults into any gaps the manager left blank.
      res = await api.post(
        `/booking-enquiries/${enquiryId}/confirm`,
        { booking_payload: payload },
        config,
      );
      toast.success(`Confirmed ${res.data.booking_code}`);
    } else {
      res = await api.post("/bookings", payload, config);
      toast.success(`Created ${res.data.booking_code}`);
    }
    backNav();
  };

  const handleSaveError = (e2) => {
    // Phase 48 — split 422 into per-field outlines + scroll-to-first.
    const { fieldErrors: fe, generalMessage } = extractFieldErrors(e2);
    setFieldErrors(fe || {});
    const firstField = Object.keys(fe || {})[0];
    if (firstField && fieldRefs.current[firstField]) {
      try { fieldRefs.current[firstField].scrollIntoView({ behavior: "smooth", block: "center" }); }
      catch { /* ignore */ }
    }
    toast.error(generalMessage || "Could not save booking");
  };

  const onConflictProceed = async () => {
    setConflictOpen(false);
    const payload = pendingPayloadRef.current;
    const config = pendingConfigRef.current;
    pendingPayloadRef.current = null;
    pendingConfigRef.current = null;
    if (!payload) return;
    setSubmitting(true);
    try {
      await commitSave(payload, config || { params: { notify_staff: notifyStaff } });
    } catch (e2) {
      handleSaveError(e2);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="page text-muted-foreground text-sm">Loading…</div>;

  return (
    <div className="page" data-testid="booking-form-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-accent">// {isEdit ? "Edit" : enquiryId ? "Confirm enquiry" : "Create"}</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2">
            {isEdit ? "Edit booking" : enquiryId ? "Confirm enquiry" : "New booking"}
          </h1>
        </div>
        <Link to="/calendar" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="back-calendar">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
      </div>

      {enquiryId && enquiry && (
        <div className="mt-4 border-2 border-amber-300 border-dashed bg-amber-50/70 rounded-md p-3 text-sm" data-testid="enquiry-conversion-banner">
          <div className="font-bold text-amber-900 uppercase tracking-wider text-[11px] mono">// Pencil-in being confirmed</div>
          <div className="mt-1 text-amber-900">
            From phone enquiry for <strong>{enquiry.customer_name}</strong> · {enquiry.customer_phone}
            {enquiry.notes && <div className="mt-1 text-xs text-amber-800/90 whitespace-pre-line line-clamp-3">{enquiry.notes}</div>}
          </div>
        </div>
      )}

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6" data-testid="booking-form">
        <div className="lg:col-span-2 bg-card border border-border rounded-[8px] p-6 space-y-6">
          <FieldGroup label="Customer" required>
            <CustomerStep value={customer} onSelect={setCustomer} />
          </FieldGroup>

          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Services <span className="text-accent">*</span></Label>
            {/* Phase P.19 — Removed redundant top multi-select. Line-items
                below ARE the multi-service picker; one row per service. */}
            <div className="space-y-3 mt-4" data-testid="line-items-list">
              {lines.map((l, i) => (
                <LineItemRow
                  key={i}
                  idx={i}
                  line={l}
                  templates={templates}
                  currency={currency}
                  onChange={(next) => updateLine(i, next)}
                  onRemove={() => removeLine(i)}
                  canRemove={lines.length > 1}
                />
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" className="mt-3" onClick={addLine} data-testid="add-line-item-button">
              <Plus className="h-3 w-3 mr-1" /> Add service
            </Button>
          </div>

          <div className="border-t border-border pt-4">
            <button
              type="button"
              onClick={() => setForm({ ...form, discountOpen: !form.discountOpen })}
              className="flex items-center gap-2 text-sm font-semibold mono uppercase tracking-widest text-muted-foreground hover:text-foreground"
              data-testid="discount-toggle"
            >
              {form.discountOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              Discount {form.discount_type !== "none" && <span className="text-accent">(active)</span>}
            </button>
            {form.discountOpen && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="discount-section">
                <FieldGroup label="Type">
                  <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                    <SelectTrigger data-testid="discount-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" data-testid="discount-type-option-none">No discount</SelectItem>
                      <SelectItem value="amount" data-testid="discount-type-option-amount">Amount off</SelectItem>
                      <SelectItem value="percent" data-testid="discount-type-option-percent">Percent off</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldGroup>
                <FieldGroup label={form.discount_type === "percent" ? "Percent (%)" : "Amount"}>
                  <Input
                    type="number"
                    min="0"
                    step={form.discount_type === "percent" ? "0.1" : "0.01"}
                    value={form.discount_value}
                    disabled={form.discount_type === "none"}
                    onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                    data-testid="discount-value-input"
                  />
                </FieldGroup>
                <FieldGroup label="Amount off">
                  <div className="h-10 px-3 rounded-[6px] border border-border bg-secondary/40 flex items-center mono font-bold" data-testid="discount-amount-display">
                    −{formatCurrency(discountAmount, currency)}
                  </div>
                </FieldGroup>
              </div>
            )}
          </div>

          <FieldGroup label="Staff" required>
            <Select value={form.staff_id} onValueChange={(v) => setForm({ ...form, staff_id: v })}>
              <SelectTrigger data-testid="staff-select"><SelectValue placeholder="Pick a staff member" /></SelectTrigger>
              <SelectContent>
                {staff.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`staff-option-${s.id}`}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FieldGroup>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FieldGroup label="Date" required>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="date-input" />
            </FieldGroup>
            <FieldGroup label="Time" required>
              <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="time-input" />
            </FieldGroup>
            <FieldGroup label="Duration (min)">
              <div className="space-y-1">
                {form.durationOverride ? (
                  <Input
                    type="number"
                    min="15"
                    step="15"
                    value={form.duration}
                    onChange={(e) => setForm({ ...form, duration: e.target.value })}
                    data-testid="duration-input"
                  />
                ) : (
                  <div
                    className="h-10 px-3 rounded-[6px] border border-border bg-secondary/40 flex items-center mono text-sm"
                    data-testid="duration-auto-display"
                  >
                    {autoDuration} min <span className="text-muted-foreground ml-2">(auto)</span>
                  </div>
                )}
                <button
                  type="button"
                  className="text-[11px] text-accent hover:underline"
                  data-testid="duration-override-toggle"
                  onClick={() => setForm({
                    ...form,
                    durationOverride: !form.durationOverride,
                    duration: form.durationOverride ? "" : String(autoDuration),
                  })}
                >
                  {form.durationOverride ? "Use auto-computed duration" : "Override duration"}
                </button>
              </div>
            </FieldGroup>
          </div>

          {requireLocation && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="booking-form-location-section">
              <FieldGroup label="Postcode / Eircode">
                <Input value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} placeholder="e.g. T12 X8YH or SW1A 1AA" data-testid="postcode-input" />
              </FieldGroup>
              <FieldGroup label="Address line">
                <Input value={form.address_line} onChange={(e) => setForm({ ...form, address_line: e.target.value })} placeholder="Street / town" data-testid="address-input" />
              </FieldGroup>
            </div>
          )}

          {requireLocation && (
            <FieldGroup label="Lat,Lng (optional, paste)">
              <Input value={form.latlng} onChange={(e) => setForm({ ...form, latlng: e.target.value })} placeholder="51.8985,-8.4756" data-testid="latlng-input" />
            </FieldGroup>
          )}

          {requireLocation && (
            <TravelPreview
              staffId={form.staff_id}
              scheduledStartIso={previewIso}
              durationMinutes={previewDuration}
              location={{
                postcode: form.postcode,
                address_line: form.address_line,
                lat: previewLatLng.lat,
                lng: previewLatLng.lng,
              }}
              excludeBookingId={isEdit ? id : null}
              className="-mt-2"
            />
          )}

          <FieldGroup label="Notes (optional)">
            <div className="flex items-center justify-end -mb-1">
              <MapsLinkHelper
                textareaRef={notesRef}
                value={form.notes || ""}
                onChange={(v) => setForm((f) => ({ ...f, notes: v }))}
              />
            </div>
            <Textarea
              ref={notesRef}
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="notes-input"
            />
          </FieldGroup>

          <FieldGroup label="Payment type">
            <Select
              value={form.payment_type}
              onValueChange={(v) => setForm({ ...form, payment_type: v })}
              onOpenChange={async (open) => {
                // Phase E — refetch on open so newly created Payment Types
                // (e.g. "Gift voucher") appear without a page reload.
                if (!open) return;
                try {
                  const { data } = await api.get("/settings/payment-types");
                  if (Array.isArray(data)) setPaymentTypes(data);
                } catch { /* silent — keep stale list */ }
              }}
            >
              <SelectTrigger data-testid="payment-type-select"><SelectValue placeholder="Pick a type" /></SelectTrigger>
              <SelectContent>
                {paymentTypes.map((p) => (
                  <SelectItem key={p.id} value={p.code} data-testid={`payment-type-option-${p.code}`}>
                    {p.name}{p.tax_rate ? ` · ${p.tax_rate}%` : ""}{p.is_default ? " · default" : ""}{p.off_the_books ? " · off-books" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FieldGroup>

          {/* Phase F — Form of payment (optional). Drives "My Cash" routing. */}
          <FieldGroup label="Form of payment (optional)">
            <Select
              value={form.paid_method_code || "__unset__"}
              onValueChange={(v) => setForm({ ...form, paid_method_code: v === "__unset__" ? "" : v })}
              onOpenChange={async (open) => {
                if (!open) return;
                try {
                  const { data } = await api.get("/settings/paid-methods");
                  if (Array.isArray(data)) setPaidMethods(data);
                } catch { /* silent */ }
              }}
            >
              <SelectTrigger data-testid="booking-paid-method-select">
                <SelectValue placeholder="— not set yet —" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__unset__" data-testid="booking-paid-method-unset">— not set yet —</SelectItem>
                {paidMethods.map((pm) => (
                  <SelectItem key={pm.id} value={pm.code} data-testid={`booking-paid-method-${pm.code}`}>
                    {pm.name}{pm.user_name ? ` · → ${pm.user_name}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FieldGroup>

          <div className="flex gap-2 border-t border-border pt-4 flex-wrap items-center">
            <Button type="submit" disabled={submitting} className="btn-cta" data-testid="booking-submit-button">
              {submitting ? "Saving..." : isEdit ? "Save changes" : "Create booking"}
            </Button>
            <Button type="button" variant="outline" onClick={backNav} data-testid="booking-cancel-button">Cancel</Button>
            {/* Phase 19: per-operation toggle for staff email dispatch */}
            <label className="flex items-center gap-2 ml-auto text-xs text-muted-foreground select-none cursor-pointer"
                   title="Suppresses the assigned/updated/cancelled email for this single change. Staff already on a job rarely need an alert for tiny tweaks.">
              <input type="checkbox" checked={notifyStaff} onChange={(e) => setNotifyStaff(e.target.checked)}
                     data-testid="booking-notify-staff-toggle"
                     className="h-4 w-4 accent-accent" />
              Notify assigned staff by email
            </label>
          </div>
        </div>

        <aside className="lg:col-span-1">
          <div className="bg-card border border-border rounded-[8px] p-5 lg:sticky lg:top-4" data-testid="booking-totals-panel">
            <h3 className="mono text-[10px] uppercase tracking-[0.25em] text-accent mb-3">// Totals</h3>
            <div className="space-y-2 text-sm">
              <Row k="Subtotal" v={formatCurrency(subtotalBefore, currency)} testid="totals-subtotal-before" />
              {form.discount_type !== "none" && (
                <Row k={`Discount${form.discount_type === "percent" ? ` (${form.discount_value}%)` : ""}`}
                     v={`−${formatCurrency(discountAmount, currency)}`}
                     muted="text-rose-700"
                     testid="totals-discount" />
              )}
              {form.discount_type !== "none" && (
                <Row k="After discount" v={formatCurrency(subtotal, currency)} testid="totals-subtotal-after" />
              )}
              <div className="h-px bg-border my-1" />
              <Row k={`Est. tax (${taxRate}%)`} v={formatCurrency(taxAmount, currency)} testid="totals-tax" />
              <div className="h-px bg-border my-1" />
              <div className="flex items-center justify-between pt-1">
                <span className="font-extrabold">Grand total</span>
                <span className="mono font-extrabold text-lg" data-testid="totals-grand-total">{formatCurrency(grandTotal, currency)}</span>
              </div>
              <div className="text-[11px] text-muted-foreground pt-2">Duration: <span className="mono font-bold">{form.durationOverride && form.duration !== "" ? form.duration : autoDuration} min</span></div>
            </div>
          </div>
        </aside>
      </form>
      <ConflictWarningDialog
        open={conflictOpen}
        onOpenChange={setConflictOpen}
        conflicts={conflicts}
        onProceed={onConflictProceed}
        onCancel={() => {
          setConflictOpen(false);
          pendingPayloadRef.current = null;
          pendingConfigRef.current = null;
        }}
      />
    </div>
  );
}

function Row({ k, v, muted, testid }) {
  return (
    <div className="flex items-center justify-between" data-testid={testid}>
      <span className={`text-muted-foreground ${muted || ""}`}>{k}</span>
      <span className={`mono font-bold ${muted || ""}`}>{v}</span>
    </div>
  );
}

function FieldGroup({ label, required, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-wider font-semibold">
        {label}{required && <span className="text-accent ml-0.5">*</span>}
      </Label>
      {children}
    </div>
  );
}
